import { useContext, useReducer, useMemo, createContext } from 'react';

import childrenPropType from '@constants/childrenPropType';

/**
 * Store factory which can be used to create a shared state within the app
 * @returns a [Provider, useStore] tuple,
 * where useStore returns a [state, setState] tuple
 */
export default function makeStore(initialState = {}) {
  // Make a context for the store
  const context = createContext();

  // Make a context provider that provides the shared state
  function Provider({ children }) {
    // Create a new state instance with useReducer
    // setState is a dispatch function that works
    // almost like "traditional" setState.
    // It accepts either an object or a callback as an argument
    const [state, setState] = useReducer((old, update) => {
      if (typeof update === 'function') {
        // If update is a function,
        // call the function with the current state as an argument,
        const updateFromCB = update(old);
        // then update state with the returned value.
        return { ...old, ...updateFromCB };
      }
      return { ...old, ...update };
    }, initialState);

    // Memoize the context value to update when the state does
    const contextValue = useMemo(() => [state, setState], [state]);

    // Provide the store to children
    return <context.Provider value={contextValue}>{children}</context.Provider>;
  }

  Provider.propTypes = { children: childrenPropType.isRequired };

  // Provide a hook to help consume the store1
  const useStore = () => useContext(context);

  // In order to use the store (shared state) anywhere in the react code,
  // the Provider must be rendered somewhere above
  // the component that uses the store.
  return [Provider, useStore];
}
